// src/data.js — createDataHelper(payload) → `d`, a null-safe accessor over the
// SubmissionVisualizationPayloadDto contract. See README.md for the full shape.

const ATTACH_IMAGE = ['image', 'drawing', 'signature'];
const ATTACH_FILE = ['document', 'file'];

// ---- schema label maps ----------------------------------------------------
// schemaJson is the full form-builder schema (JSON string). It may nest fields
// in pages/sections, and key props vary (key|name|fieldKey|id). We walk the
// parsed object recursively and collect any object carrying a key-like prop AND
// a `label`, building key→label and label→key (lower-cased) maps.
function parseSchema(schemaJson) {
  if (!schemaJson || typeof schemaJson !== 'string') return null;
  try {
    return JSON.parse(schemaJson);
  } catch {
    return null;
  }
}

function pickKey(obj) {
  if (!obj || typeof obj !== 'object') return null;
  for (const prop of ['fieldKey', 'key', 'name', 'id']) {
    const v = obj[prop];
    if (typeof v === 'string' && v.length) return v;
  }
  return null;
}

function buildLabelMaps(schemaObj) {
  const keyToLabel = new Map(); // exact key → label
  const labelToKey = new Map(); // lower(label) → key
  const seen = new Set();

  function walk(node) {
    if (!node || typeof node !== 'object') return;
    if (seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      for (const item of node) walk(item);
      return;
    }

    const key = pickKey(node);
    const label = typeof node.label === 'string' ? node.label : null;
    if (key && label) {
      if (!keyToLabel.has(key)) keyToLabel.set(key, label);
      const lk = label.toLowerCase();
      if (!labelToKey.has(lk)) labelToKey.set(lk, key);
    }

    for (const v of Object.values(node)) {
      if (v && typeof v === 'object') walk(v);
    }
  }

  walk(schemaObj);
  return { keyToLabel, labelToKey };
}

// ---- value coercion helpers ----------------------------------------------
function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function toText(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(toText).filter((s) => s !== '').join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function isAttachmentArray(value) {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((x) => x && typeof x === 'object' && typeof x.url === 'string')
  );
}

// ---- main factory ---------------------------------------------------------
export function createDataHelper(payload, assets) {
  const safe = payload && typeof payload === 'object' ? payload : {};
  const assetMap = assets && typeof assets === 'object' ? assets : {};
  const fieldsArr = Array.isArray(safe.fields) ? safe.fields : [];
  const schemaWrapper = safe.schema && typeof safe.schema === 'object' ? safe.schema : {};
  const schemaObj = parseSchema(schemaWrapper.schemaJson);
  const { keyToLabel, labelToKey } = buildLabelMaps(schemaObj);

  // Index fields by exact key and by lower-cased key for fast lookup.
  const byKey = new Map();
  const byKeyLower = new Map();
  for (const f of fieldsArr) {
    if (!f || typeof f !== 'object') continue;
    const k = typeof f.fieldKey === 'string' ? f.fieldKey : null;
    if (k === null) continue;
    if (!byKey.has(k)) byKey.set(k, f);
    const lk = k.toLowerCase();
    if (!byKeyLower.has(lk)) byKeyLower.set(lk, f);
  }

  function _find(keyOrLabel) {
    if (keyOrLabel === null || keyOrLabel === undefined) return undefined;
    const q = String(keyOrLabel);
    // 1) exact fieldKey
    if (byKey.has(q)) return byKey.get(q);
    // 2) label → key (case-insensitive)
    const mappedKey = labelToKey.get(q.toLowerCase());
    if (mappedKey && byKey.has(mappedKey)) return byKey.get(mappedKey);
    // 3) case-insensitive fieldKey
    const lk = byKeyLower.get(q.toLowerCase());
    if (lk) return lk;
    return undefined;
  }

  function label(keyOrLabel) {
    if (keyOrLabel === null || keyOrLabel === undefined) return '';
    const q = String(keyOrLabel);
    if (keyToLabel.has(q)) return keyToLabel.get(q);
    const f = _find(q);
    if (f && keyToLabel.has(f.fieldKey)) return keyToLabel.get(f.fieldKey);
    return q;
  }

  function value(keyOrLabel) {
    const f = _find(keyOrLabel);
    return f ? (f.value ?? null) : null;
  }

  function text(keyOrLabel) {
    return toText(value(keyOrLabel));
  }

  function num(keyOrLabel) {
    const v = value(keyOrLabel);
    if (v === null || v === undefined || v === '') return null;
    const n = Number(Array.isArray(v) ? v[0] : v);
    return Number.isNaN(n) ? null : n;
  }

  function has(keyOrLabel) {
    const f = _find(keyOrLabel);
    return !!f && !isEmpty(f.value);
  }

  function attachments(keyOrLabel) {
    const v = value(keyOrLabel);
    return isAttachmentArray(v) ? v : [];
  }

  function allAttachmentArrays() {
    const out = [];
    for (const f of fieldsArr) {
      if (f && isAttachmentArray(f.value)) out.push(...f.value);
    }
    return out;
  }

  function filterByType(keyOrLabel, types) {
    const list =
      keyOrLabel === undefined || keyOrLabel === null
        ? allAttachmentArrays()
        : attachments(keyOrLabel);
    return list.filter((a) => a && types.includes(String(a.type || '').toLowerCase()));
  }

  function images(keyOrLabel) {
    return filterByType(keyOrLabel, ATTACH_IMAGE);
  }
  function files(keyOrLabel) {
    return filterByType(keyOrLabel, ATTACH_FILE);
  }
  function audio(keyOrLabel) {
    return filterByType(keyOrLabel, ['voice', 'audio']);
  }
  function video(keyOrLabel) {
    return filterByType(keyOrLabel, ['video']);
  }

  function repeated(keyOrLabel) {
    const v = value(keyOrLabel);
    if (!Array.isArray(v)) return [];
    return v.map((entry, i) => {
      const childFields = Array.isArray(entry?.fields) ? entry.fields : [];
      // Reuse the same schema for label maps so child helpers resolve labels; carry assets so d.asset works too.
      const childHelper = createDataHelper({ ...safe, fields: childFields }, assetMap);
      return {
        ...entry,
        instanceIndex: typeof entry?.instanceIndex === 'number' ? entry.instanceIndex : i,
        fields: childFields,
        h: childHelper
      };
    });
  }

  function series(repeatedKeyOrLabel, fieldKeyOrLabel) {
    const entries = repeated(repeatedKeyOrLabel);
    const sorted = [...entries].sort((a, b) => (a.instanceIndex ?? 0) - (b.instanceIndex ?? 0));
    const out = [];
    for (const e of sorted) {
      const n = e.h ? e.h.num(fieldKeyOrLabel) : null;
      if (n !== null && Number.isFinite(n)) out.push(n);
    }
    return out;
  }

  function labResults() {
    return Array.isArray(safe.labResults) ? safe.labResults : [];
  }

  // Coordinates for a Location field (value {latitude,longitude} / {lat,lng}), falling back to the
  // submission's own lat/long. Returns {lat,lng} numbers, or null when neither has valid coordinates.
  // Feed straight into the Map/Location gadgets — never print a raw location object as text (TASK-0238).
  function geo(keyOrLabel) {
    let lat;
    let lng;
    if (keyOrLabel !== undefined && keyOrLabel !== null) {
      const v = value(keyOrLabel);
      const o = Array.isArray(v) ? v[0] : v;
      if (o && typeof o === 'object') {
        lat = o.latitude ?? o.lat ?? o.Latitude;
        lng = o.longitude ?? o.lng ?? o.lon ?? o.Longitude;
      }
    }
    const m = safe.submission && typeof safe.submission === 'object' ? safe.submission : {};
    if (lat == null) lat = m.latitude;
    if (lng == null) lng = m.longitude;
    const a = Number(lat);
    const b = Number(lng);
    return Number.isFinite(a) && Number.isFinite(b) ? { lat: a, lng: b } : null;
  }

  // Uploaded image assets: name → data-URI (server-inlined). '' when missing so an <img src> degrades cleanly.
  function asset(name) {
    return (name && Object.prototype.hasOwnProperty.call(assetMap, name)) ? assetMap[name] : '';
  }

  return {
    raw: safe,
    meta: safe.submission && typeof safe.submission === 'object' ? safe.submission : {},
    // The submission's linked work order (payload.workOrder, sv7+), or null when there is none /
    // the payload predates it. Views must null-check: d.workOrder?.status.
    workOrder: safe.workOrder && typeof safe.workOrder === 'object' ? safe.workOrder : null,
    schema: schemaObj,
    formName: schemaWrapper.name || (safe.submission && safe.submission.formSchemaId) || 'Submission',
    fields: () => fieldsArr,
    _find,
    value,
    text,
    num,
    label,
    has,
    attachments,
    images,
    files,
    audio,
    video,
    repeated,
    series,
    labResults,
    geo,
    asset,
    attachmentsAll: allAttachmentArrays
  };
}

export default createDataHelper;
